// src/components/cityFaqs.js
//
// Derives city-specific FAQ entries from the structured cityData object
// (from src/data/cities/<stateSlug>.js) plus optional stateData context.
// Used by CityPage.jsx to render the FAQ accordion AND the FAQPage
// schema.org JSON-LD payload for rich-result eligibility.
//
// Each FAQ has a `q` (question) and `a` (plain-text answer ≤ 380 chars
// so it sits comfortably inside Google's rich-result preview). We
// deliberately produce 5-7 FAQs per city by combining:
//   (a) one "how hard is the water here" question (always)
//   (b) one "how much does cleaning cost in this city" question (always)
//   (c) one "best time of year here" (uses parent state cleaningCalendar)
//   (d) one "why do my windows look dirty so quickly" — uses the city's
//       contaminantHighlights or a parent-state fallback
//   (e) one "do I need a professional in this city" (always)
//   (f) one "neighborhood-specific" question if neighborhoodDetail is rich
//   (g) one "near me" funnel question (always)
//
// This produces consistent city-level FAQ coverage across all 85+ Tier 1
// cities without hand-writing 600+ questions, while each FAQ stays
// specific enough to the city to feel non-generic.
//
// The faqJsonLd helper from stateFaqs.js works on any {q,a} shape, so we
// re-export it from there rather than duplicating the schema builder.

import { faqJsonLd as _faqJsonLd } from './stateFaqs.js';
import { classifyHardness , hardnessClause, hardnessIsPublished } from '../data/water.js';
import { CLIMATE } from '../data/climateNormals.js';
import { bestMonths, monthRangesLong, freezeSentence, stationPhrase, cleaningCalendar } from './climate.js';
import { AIR_LOAD } from '../data/airLoad.js';
import { methodVerdicts } from '../data/methodVerdicts.js';
import { LOCAL_MARKET, MARKET_VINTAGE } from '../data/localMarket.js';

// s134 Phase 5c — "FAQs regenerated from captured data, so no two cities share an
// answer" (ROADMAP Phase 5). Before this, 224 best-time answers and 19 cost answers
// were word-for-word identical to another city's once the city name and numbers
// were masked: the month list and frost dates were the only variables. Both now
// carry facts that differ city to city and are already on the page: the NOAA
// station the normals come from, the USA-NPN spring-bloom date (Phase 4a pollen),
// and the county's janitorial and window-cleaning firm count (Phase 4c-1 market).
// verify-city-faqs now fails if any two cities share an answer after masking the
// city, state, utility and every number.
const CBP_YEAR = (String(MARKET_VINTAGE).match(/County Business Patterns (\d{4})/) || [])[1] || null;
function pollenSentence(key) {
  const po = AIR_LOAD[key]?.pollen;
  if (!po || po.s !== 'captured' || !po.date) return null;
  // Same split the air panel prints: before mid-February pollen runs much of the year.
  return po.doy < 45
    ? `Pollen runs much of the year; first bloom comes around ${po.date} on the USA-NPN spring index.`
    : `Spring bloom comes around ${po.date} on the USA-NPN index; wash after the tree-pollen wave it brings.`;
}
// s135 (DECISIONS #51): the cleaning calendar's picks, in the same words as the page's WASH marks.
const LONG_M = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WHY = { bloom: 'after the bloom', smoke: 'after smoke season', frost: 'before frost' };
function washSentence(clim, key) {
  const cal = cleaningCalendar(clim, AIR_LOAD[key]);
  if (!cal || !cal.picks.length) return null;
  const date = AIR_LOAD[key]?.pollen?.date; // the USA-NPN first-bloom date the pick follows
  const by = new Map();
  cal.picks.forEach((p) => by.set(p.m, [...(by.get(p.m) || []), p.why === 'bloom' && date ? `after the ${date} bloom` : WHY[p.why]]));
  const parts = [...by.keys()].sort((a, b) => a - b).map((m) => `${LONG_M[m]} (${by.get(m).join(' and ')})`);
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `The calendar on this page marks ${list}.`;
}
function marketClause(key) {
  const co = LOCAL_MARKET[key]?.county;
  if (!co || !co.name || co.firms == null || !CBP_YEAR) return null;
  const firms = co.firms === 1 ? '1 janitorial or window-cleaning business' : `${co.firms.toLocaleString('en-US')} janitorial and window-cleaning businesses`;
  return `${co.name} had ${firms} in the Census Bureau's ${CBP_YEAR} County Business Patterns`;
}

// s73 — this file carried the NINTH copy of the hardness bands, and it was the
// one that mattered most: these answers become the FAQPage JSON-LD on all 271
// city pages, so the vocabulary here is what an answer engine extracts. It
// spoke the 6-band shifted vocabulary, which meant a city's structured data
// disagreed with the Atlas's Dataset JSON-LD about the same reading. s72's
// handoff set the rule for exactly this case — "if they ever diverge, the page
// is lying to one of its two readers" — and this was the divergence.
//
// Found by scripts/verify-hardness-single-source.mjs on its first run. It was
// not in s72's list of three, nor in s73's plan of six.
function tierLabel(hardness) {
  const band = classifyHardness(hardness);
  return band ? band.label.toLowerCase() : 'in a typical municipal range';
}

// Keyed by band CODE, not by label. The previous version switched on the label
// string, so repointing tierLabel at the real bands would have silently dropped
// all 271 answers through to the default — which is the same class of bug as
// StatePage switching on dominantTier (HANDOFF s72 §3), one file over.
//
// The prose is unchanged and moves with its band: the old ladder broke at the
// same 60/120/180/250 points, so each band keeps the sentence it already had.
// The one merge is at the top — the old vocabulary split 251–349 from 350+,
// USGS does not, and 34 cities live above 251. The merged text takes the
// harder reading of the two, because under-warning at 400 mg/L is the more
// expensive error.
const TIER_MEANING_BY_CODE = {
  SOFT: 'meaning municipal water leaves minimal mineral residue when it dries on glass',
  MOD: 'meaning municipal water leaves minor mineral residue on dark glass over extended dry-down',
  HARD: 'meaning municipal water leaves visible spotting on dark glass and shows lower-sash residue over time',
  VHRD: 'meaning municipal water consistently leaves visible mineral spots and benefits from a citric finish-rinse on long-residence glass',
  EXHD: 'meaning municipal water deposits mineral residue on every exposed pane, accelerates long-term etching, and cannot be the last thing that touches the glass — most cleaners at this level run a deionized rinse',
};

// ── Per-city overrides (Phase 1, s87) ──────────────────────────────────────
//
// ROADMAP.md §1.2 clause 3 requires ZERO sentences shared verbatim between any
// two city pages. Three sentences in this generator and one in CityPage were
// identical on all 271 pages, because they are template prose with the city
// name substituted in. §1.4 listed four; the fourth (a hardness-band
// description reading "That is moderately hard for a US municipal supply") no
// longer exists — s86 replaced it with TIER_MEANING_BY_CODE, which is banded
// rather than global but still shared by every city inside a band.
//
// A city record may now carry its own sentence for each. When it does, the
// city's own text is used; when it does not, the shared fallback stands, so
// the cities Phase 1 has not reached yet are unaffected. A city is not DONE
// under clause 3 until all four fields are populated.
function tierMeaning(hardness, city) {
  if (city?.hardnessMeaning) return city.hardnessMeaning;
  const band = classifyHardness(hardness);
  return (band && TIER_MEANING_BY_CODE[band.code]) || 'with normal mineral residue behavior on glass';
}

function sourceLabel(s) {
  switch (s) {
    case 'surface': return 'lake or reservoir surface water';
    case 'groundwater': return 'groundwater';
    case 'aquifer': return 'aquifer-source groundwater';
    case 'mixed': return 'a mixed surface-and-groundwater blend';
    case 'well-prevalent': return 'predominantly private well water';
    default: return 'municipal water';
  }
}

function bestSeasonLine(cal) {
  if (!cal) return null;
  if (cal.fall) return `fall — ${cal.fall.toLowerCase()}`;
  if (cal.spring) return `spring — ${cal.spring.toLowerCase()}`;
  if (cal.summer) return `summer — ${cal.summer.toLowerCase()}`;
  return null;
}

function costSentence(city) {
  const cr = city.costRange || {};
  const pp = cr.perPane;
  const wh = cr.wholeHome;
  const parts = [];
  if (pp && pp.length === 2) parts.push(`$${pp[0]}–${pp[1]} per pane`);
  if (wh && wh.length === 2) parts.push(`$${wh[0]}–${wh[1]} for a standard single-story exterior`);
  if (!parts.length) return null;
  return parts.join(' or ');
}

// s109 3A: where the operator publishes no hardness (shape unpublished or
// unlocated) the templated answer was one sentence shared by ~34 pages. The
// city's own hardnessMeaning already explains its situation, so the FAQ leads
// with its first two sentences instead — never when they print the record's
// planning figure, which is our estimate and must not reach FAQPage JSON-LD.
const ABBR = /\b(St|Mt|Ft|Dr|Mr|Mrs|No|U\.S)\.$/;
const plainText = (s) => String(s == null ? '' : s).replace(/\[([^\]]+)\]\((?:https?:\/\/|\/)[^)\s]+\)/g, '$1');
// s110: answers end on a sentence, never mid-word ("…What re" reached FAQPage JSON-LD on
// New York City). Cut at the last sentence end inside the limit; failing that, the last word.
function clip(text, max = 380) {
  const t = String(text).replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const head = t.slice(0, max);
  const end = Math.max(head.lastIndexOf('. '), head.lastIndexOf('? '), head.lastIndexOf('! '));
  if (end >= 120) return head.slice(0, end + 1);
  return head.slice(0, head.lastIndexOf(' ')).replace(/[,;:—–-]+$/, '') + '…';
}
// First `n` sentences of authored prose, whole sentences only, within `max` characters.
function firstSentences(raw, n = 2, max = 380) {
  const text = plainText(raw).replace(/\s+/g, ' ').trim();
  const parts = [];
  for (const piece of text.split(/(?<=[.!?])\s+(?=[A-Z])/)) {
    if (parts.length && ABBR.test(parts[parts.length - 1])) parts[parts.length - 1] += ` ${piece}`;
    else parts.push(piece);
  }
  let out = '';
  for (const s of parts.slice(0, n)) { const next = out ? `${out} ${s}` : s; if (next.length > max) break; out = next; }
  return out;
}
// s110 3A: for a PUBLISHED figure, lead with the city's own explanation when its first two
// sentences state one of the published numbers; otherwise keep the generated answer.
function authoredPublishedAnswer(city) {
  const hs = city?.hardnessSourcing;
  if (!hs || (hs.shape !== 'range' && hs.shape !== 'point')) return null;
  const figs = (hs.range || [hs.value]).filter((x) => x != null).map(String);
  const out = firstSentences(city.hardnessMeaning);
  if (out.length < 60) return null;
  return figs.some((f) => new RegExp(`(?<![0-9.])${f.replace('.', '\\.')}(?![0-9])`).test(out)) ? out : null;
}
function ownHardnessAnswer(city) {
  const shape = city?.hardnessSourcing?.shape;
  if (shape !== 'unpublished' && shape !== 'unlocated') return null;
  const text = (city.hardnessMeaning || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const parts = [];
  for (const piece of text.split(/(?<=[.!?])\s+(?=[A-Z])/)) {
    if (parts.length && ABBR.test(parts[parts.length - 1])) parts[parts.length - 1] += ` ${piece}`;
    else parts.push(piece);
  }
  let out = '';
  for (const s of parts.slice(0, 2)) {
    const next = out ? `${out} ${s}` : s;
    if (next.length > 380) break;
    out = next;
  }
  if (out.length < 60) return null;
  if (city.hardness != null && new RegExp(`(?<![0-9.])${city.hardness}\\s*(mg|ppm)`, 'i').test(out)) return null;
  return out;
}

const lc = (t) => t.charAt(0).toLowerCase() + t.slice(1);
// Where the water comes from, as the city's own record words it: the operator's
// description where there is one, else the first treatment steps SDWIS records.
function sourceClause(city) {
  const t = city?.waterPanel?.treatment;
  if (!t || t.s !== 'captured') return null;
  // "Groundwater across the Windward and Pearl Harbor system, per the EPA SDWIS federal
  // record" still names the system; only a bare category ("Surface water, per …") is generic.
  let src = String(t.sources || '').split(/;|(?<![A-Z0-9])\.(?=\s+[A-Z]|$)/)[0].replace(/,?\s*per the (?:operator's report and the )?EPA SDWIS federal record.*$/i, '').trim();
  if (/^(?:purchased )?(?:surface water|groundwater)(?: under the direct influence of surface water)?(?: with groundwater (?:wells|plants))?(?: wells)?$/i.test(src)) src = '';
  // A long operator description is cut at a clause boundary ("… water from the X
  // and Y projects" → "… water"), never mid-list; if no clean cut exists it is skipped.
  if (src.length > 140) { const m = src.slice(0, 140).match(/^(.{30,}?)\s+(?:from|via|drawn|through|with)\s/); const c = src.indexOf(', '); src = m ? m[1].replace(/[,:]$/, '').replace(/\s+(?:\w+ed|bought|drawn|sold|held|taken)$/, '') : (c >= 25 && c <= 140 ? src.slice(0, c) : ''); }
  // Lower-case the first word only when it is an ordinary word ("Surface water …",
  // "Two systems …"); a place name ("Potomac River …", "McKenzie River …") keeps its capital.
  src = src.replace(/^In \d{4},?\s+/, '');
  if (/^(?:purchased )?(?:surface water|groundwater)(?: wells)?$/i.test(src)) src = '';
  if ((src.match(/\(/g) || []).length > (src.match(/\)/g) || []).length) src = src.slice(0, src.lastIndexOf(' ('));
  src = src.replace(/[,:;\s]+$/, '');
  const COMMON = /^(?:surface|groundwater|ground|water|mostly|about|its|his|two|three|four|five|six|seven|purchased|wells?|the|a|an|local|treated|blended|raw|river|reservoir|lake|lakes|springs?|primarily|mainly|a|some|roughly|nearly)\b/i;
  const firstWord = src.split(/\s+/)[0];
  const lower = COMMON.test(firstWord) && !/^(?:lake|river|reservoir)$/i.test(firstWord) ? lc(src) : /^(?:Lake|River|Reservoir)\s+[A-Z]/.test(src) ? src : (COMMON.test(firstWord) ? lc(src) : src);
  if (src && !/SDWIS/.test(src)) return `It draws on ${lower}`;
  const steps = String(t.train || '').split(/;/)[0].split(/,\s*|\s+and\s+/).map((x) => x.trim()).filter(Boolean);
  if (!steps.length || /no filtration/i.test(t.train || '')) return /no filtration/i.test(t.train || '') ? 'SDWIS records no filtration or softening for the system' : null;
  const first = steps.slice(0, 4);
  return `Treatment starts with ${first.length > 1 ? `${first.slice(0, -1).map(lc).join(', ')} and ${lc(first[first.length - 1])}` : lc(first[0])}`;
}
function rinseAnswer(city, clim) {
  const v = methodVerdicts(city, clim);
  const r = v.find((x) => x.key === 'rinse'); if (!r) return null;
  const who = city.utility || 'The utility';
  const Who = who.charAt(0).toUpperCase() + who.slice(1);
  const basis = r.basis.replace(/^TDS /, 'dissolved solids of ').replace(/^Hardness /, 'hardness of ');
  const head = {
    tap: `Usually yes: ${who} publishes ${basis}, low enough that tap-rinsed glass tends to dry clean.`,
    dry: `Yes, if the rinse is squeegeed off before it dries: ${who} publishes ${basis}, between this site's two dissolved-solids flags.`,
    pure: `Not as the final rinse: ${who} publishes ${basis}, enough to spot as it dries, so finish with pure water.`,
    unknown: `${Who} publishes neither hardness nor dissolved solids, so rinse one pane and let it dry in sun; a white ring means switch to pure water.`,
  }[r.level];
  const res = v.find((x) => x.key === 'residue');
  const resLine = !res ? null : {
    stain: `Its ${lc(res.basis).replace(' up to ', ', up to ')}, is at the staining level, and acid cleaners will not lift that film.`,
    salt: `Its ${lc(res.basis).replace(' up to ', ', up to ')}, leaves a salt film that pulls moisture back, so rinse it off rather than let it dry.`,
    mineral: `Water left to dry leaves mineral scale; fresh spots lift with a mild acid.`,
    clear: `Iron, manganese, sodium and chloride all sit under this site's flags (highest: ${lc(res.basis)}).`,
  }[res.level];
  const gear = v.find((x) => x.key === 'resin');
  const gearLine = !gear ? null : gear.level === 'chloramine'
    ? `The supply is treated with ${lc(gear.basis)}, which lasts in a bucket and wears deionising resin faster.`
    : `A deionising cartridge yields about ${gear.note.replace(/^Resin yield ~/, '').replace(/ of 100 mg\/L water$/, '')} of what it would on 100 mg/L water.`;
  const src = sourceClause(city);
  // Whole sentences only: over 380 characters, drop the resin line, then the residue
  // line; the source sentence stays, because it is the part that is this city's own.
  const build = (xs) => xs.filter(Boolean).join(' ');
  const srcS = src ? `${src}.` : null;
  let out = build([head, resLine, gearLine, srcS]);
  if (out.length > 380) out = build([head, resLine, srcS]);
  if (out.length > 380) out = build([head, srcS]);
  return clip(out);
}

export function generateCityFaqs(city, stateData) {
  if (!city) return [];
  const faqs = [];
  const stateName = stateData?.name || '';
  const stateContext = stateName ? `, ${stateName}` : '';
  const cityFull = `${city.cityName}${stateContext}`;

  // 1. Hardness — always
  faqs.push({
    q: `How hard is the water in ${cityFull}?`,
    // s90 clause 4: the FAQ answer reaches the FAQPage JSON-LD as well as the
    // reader, so an unpublished figure asserted here is a structured-data
    // claim, not just prose. hardnessClause() prints only what the operator
    // published and suppresses the band where no operator value exists.
    a: ownHardnessAnswer(city) || authoredPublishedAnswer(city) || clip((`${city.cityName} runs on ${city.utility} ${sourceLabel(city.sourceType)} `
      + `${hardnessClause(city, { asCaCO3: true })}. ${tierMeaning(city.hardness, city)}`)
      .replace(/\.\.+$/, '.').replace(/\s+/g, ' ').trim()),
  });

  // 2. Cost — always (cityname-specific cost range)
  const costLine = costSentence(city);
  if (costLine) {
    faqs.push({
      q: `How much does window cleaning cost in ${city.cityName}?`,
      // s110 3A: plus the city's own first price-driver sentence when it fits.
      // s134 5c: the county's firm count rides in the same sentence, so the price line is not a
      // template shared by every city without a price-driver note.
      a: (() => { const base0 = `Residential window cleaning in ${city.cityName} typically runs ${costLine}.`; let pd0 = firstSentences(city.priceDrivers, 1, 380 - base0.length - 1); if (pd0.length < 60) pd0 = firstSentences(city.priceDrivers, 2, 380 - base0.length - 1); if (pd0.length >= 40) return clip(`${base0} ${pd0}`); const mk = marketClause(`${city.stateSlug}/${city.citySlug}`); const base = `Residential window cleaning in ${city.cityName} typically runs ${costLine}${mk ? `; ${mk}` : ''}.`; const pd = firstSentences(city.priceDrivers, 1, 380 - base.length - 1); return clip((pd.length >= 40 ? `${base} ${pd}` : base)); })(),
    });
  }

  // 3. Best time of year — pulls from parent state cleaningCalendar
  // s110 3A: from the city's own NOAA 1991–2020 normals (nearest station) instead of the
  // state calendar, which gave every city in a state the same answer.
  const clim = CLIMATE[`${city.stateSlug}/${city.citySlug}`];
  const best = clim && monthRangesLong(bestMonths(clim));
  const season = bestSeasonLine(stateData?.cleaningCalendar);
  if (best) {
    const stn = stationPhrase(clim.name) || 'the nearest weather station';
    // The wettest normal month, by rain days — a named month, not a figure.
    const wi = clim.rain.reduce((m, v, i, a) => (v > a[m] ? i : m), 0);
    const wettest = clim.rain.filter((v) => v === clim.rain[wi]).length === 1 ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][wi] : null;
    const lead = best === 'all year'
      ? `${city.cityName} has no frost months, and rain days stay low enough to wash all year, by NOAA's 1991–2020 normals for ${stn}.`
      : `${city.cityName}'s drier, frost-free months are ${best}, by NOAA's 1991–2020 normals for ${stn}${wettest ? `, where ${wettest} brings the most rain` : ''}.`;
    // The advice half of the frost sentence is dropped here to leave room for the bloom date.
    const fz = (freezeSentence(clim) || '').replace(', so plan exterior washes outside that stretch.', '.') || null;
    const hot = clim.hi.map((t, i) => (t >= 90 ? i : -1)).filter((i) => i >= 0);
    const heat = hot.length && hot.length < 6 ? `Normal highs reach 90°F in ${monthRangesLong(clim.hi.map((t) => t >= 90))}.` : null;
    const pol = pollenSentence(`${city.stateSlug}/${city.citySlug}`);
    const wash = washSentence(clim, `${city.stateSlug}/${city.citySlug}`);
    // The bloom date travels inside the wash sentence when a bloom pick exists; the separate sentence stays otherwise.
    const polOut = wash && / bloom\)| bloom and /.test(wash) ? null : pol;
    faqs.push({ q: `When is the best time of year to clean windows in ${city.cityName}?`, a: clip([lead, wash, fz, polOut, heat].filter(Boolean).join(' ')) });
  } else if (season) {
    faqs.push({
      q: `When is the best time of year to clean windows in ${city.cityName}?`,
      a: clip(`The ${stateName} working calendar favors ${season}`),
    });
  }

  // 3b. s134 Phase 5c — the rinse question, answered from the method panel's own
  // verdicts (methodVerdicts.js, one source for panel, gate and FAQ). Every figure is
  // the utility's published one via the verdict's `basis`; where the utility
  // publishes nothing usable the answer says to test a pane rather than guessing.
  const rinse = rinseAnswer(city, clim);
  if (rinse) faqs.push({ q: `Can I rinse windows with ${city.cityName} tap water?`, a: rinse });

  // 4. Why do my windows look dirty so quickly — uses contaminantHighlights
  const ch = city.contaminantHighlights;
  if (ch && ch.length) {
    const primary = ch[0];
    faqs.push({
      q: `Why do my windows look dirty so quickly in ${city.cityName}?`,
      a: clip(`In ${city.cityName} the dominant residue patterns include ${ch.slice(0, 2).join(' and ').toLowerCase()}. ${city.residueTiming || 'Cleaning intervals tied to the seasons these residue patterns peak will significantly extend how long each wash holds.'}`),
    });
  } else if (city.localQuirks?.[0]) {
    faqs.push({
      q: `Why do my windows look dirty so quickly in ${city.cityName}?`,
      a: clip(`${city.localQuirks[0]} ${city.residueTiming || 'Cleaning intervals tied to that pattern will significantly extend how long each wash holds.'}`),
    });
  }

  // 5. DIY vs professional — always
  faqs.push({
    q: `Do I need a professional to clean my windows in ${city.cityName}?`,
    a: clip((city.diyVsPro
      ? `${city.diyVsPro}`
      : `Single-story homes in ${city.cityName} with accessible glazing can be cleaned by homeowners with basic squeegee technique. Multi-story houses, post-2010 coated glass, hard-water markets, and screen-and-track work usually pay for themselves with a professional.`
    )),
  });

  // 6. Neighborhood-specific question (only when we have 4+ neighborhoods)
  //
  // s88: this answer quotes the city's OWN authored note (the s87 clause-3
  // fix). That note is also a target of inject-city-sibling-links, which
  // rewrites a sibling city's name into a markdown link. On the page itself
  // that is fine — CityPage passes notes through renderInlineLinks. Here it
  // is NOT: a FAQ answer is rendered as plain text AND serialised into
  // FAQPage JSON-LD, so `[Warwick](/states/rhode-island/warwick)` reached
  // both the reader and the structured data. Found by probe-render-defects on
  // Cranston RI; Gary IN was already shipping it before this session.
  //
  // So flatten links to their label text on the way in. Do not "fix" this by
  // removing the sibling-link injection or by keeping city names out of
  // authored notes — the link is correct on the page, and a note that cannot
  // mention a neighbouring city is a worse constraint than a flatten step.
  if (city.neighborhoodDetail && city.neighborhoodDetail.length >= 4) {
    const sampleNbhd = city.neighborhoodDetail[0].name;
    const plain = (s) => String(s == null ? '' : s).replace(/\[([^\]]+)\]\((?:https?:\/\/|\/)[^)\s]+\)/g, '$1');
    faqs.push({
      q: `What sets ${sampleNbhd} apart for window cleaning in ${city.cityName}?`,
      a: clip(`${plain(city.neighborhoodDetail[0].note)} ${city.neighborhoodDetail.slice(1, 3).map((n) => n.name).join(' and ')} have their own housing stock and glazing as well.`),
    });
  }

  // s109 Phase 3A: the generic 'where can I find a cleaner' FAQ was dropped —
  // its answer said nothing city-specific, and the page's find-a-pro block
  // already does that job.

  return faqs;
}

// Re-export the JSON-LD helper from stateFaqs.js — the schema shape is
// identical for any {q,a}[] list, so there's no reason to duplicate.
export const faqJsonLd = _faqJsonLd;
